import { useState, useEffect, useCallback } from 'react';
import type { Session } from '../types/session.types';
import * as sessionsApi from '../api/sessions';

export function useSessions(workspaceId: string | null, autoLoad = true) {
  const [sessions, setSessions] = useState<Session[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadSessions = useCallback(async () => {
    if (!workspaceId) return;

    setLoading(true);
    setError(null);

    try {
      const fetched = await sessionsApi.fetchSessions(workspaceId, { limit: 50, offset: 0 });
      setSessions(fetched);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to load sessions';
      console.error('[useSessions]', message, err);
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [workspaceId]);

  const deleteSession = useCallback(async (sessionId: string) => {
    await sessionsApi.deleteSession(sessionId);
    setSessions((prev) => prev.filter((s) => s.id !== sessionId));
  }, []);

  useEffect(() => {
    if (autoLoad && workspaceId) {
      loadSessions();
    }
  }, [autoLoad, workspaceId, loadSessions]);

  return { sessions, loading, error, loadSessions, deleteSession };
}
